import type { RouteIds } from '@tanstack/react-router';
import { createRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { logError } from '@core/logging';
import { createApiRuntime, registerRuntimeResolver } from '@core/swagger-client';
import { DefaultCatchBoundary, isBaseApiResponse } from './components/ErrorPage';
import { routeTree } from './routeTree.gen';
import { findRuntime, rememberRuntime } from './utils/apiRuntimeRegistry';
import { getScrollRestorationKey } from './utils/backNavigation';
import { getAuthorizationToken } from './utils/getAuthorizationToken';
import { getBackendOrigin, getSiteSettings } from './utils/getSiteSettings';

// Log lines find "the current runtime" through this: the request being rendered on the server, the
// app's own runtime in the browser.
registerRuntimeResolver(findRuntime);

export const getRouter = () => {
    const siteSettings = getSiteSettings();
    // One runtime per request on the server (Start calls getRouter for every request) and one for
    // the app in the browser. The server signs in with the request's own AuthSession cookie, the
    // browser with the token it stored.
    const runtime = createApiRuntime({ origin: getBackendOrigin(), token: getAuthorizationToken() });
    rememberRuntime(runtime);
    const queryClient = runtime.queryClient;

    const router = createRouter({
        routeTree,
        context: { queryClient, siteSettings, runtime },
        defaultPreload: 'intent',
        notFoundMode: 'root',
        scrollRestoration: true,
        getScrollRestorationKey,
        defaultErrorComponent: DefaultCatchBoundary,
        // The error boundary renders a page but says nothing, so a render error never
        // reached Seq. API errors already get their own page and are not app failures,
        // so they stay out of here.
        defaultOnCatch: (error, errorInfo) => {
            if (isBaseApiResponse(error)) return;

            // A boundary catches whatever was thrown, and that need not be an Error.
            // Anything else still has to reach Seq, just without a stack to show.
            const caught = error instanceof Error ? error : undefined;

            logError('Route error boundary caught: {Message}', {
                Message: caught?.message ?? String(error),
                Name: caught?.name ?? 'UnknownThrownValue',
                Stack: caught?.stack,
                ComponentStack: errorInfo.componentStack,
                Uri: globalThis.window?.location?.href
            });
        }
    });

    setupRouterSsrQueryIntegration({
        router: router,
        queryClient: queryClient,
        handleRedirects: true
    });

    return router;
};

export type RouterType = ReturnType<typeof getRouter>;
export type RouterIds = RouteIds<RouterType['routeTree']>;
